import { Offer, ProjectSituation } from "src/app/domains/internal";

import type { EvaCompany } from "../features/eva-company/domains/eva-company";
import { Tag } from "../features/eva-company/domains/eva-company-tag";
import {
  PurchaseOrder,
  PurchaseOrderStatus,
  PurchaseOrderType,
  PurchaseOrderWorkForceType,
} from "../features/purchase-order/domains/purchase-order";
import { Employee, EmployeeActivityType, Invoice, InvoiceStatus, OfferUnitType } from "./";
import { Asset } from "./asset";
import { EmployeeWeekActivity, EmployeeWeekActivityStatus } from "./employee-week-activity";
import { OfferStatus } from "./offer";
import { EvaAddress } from "./address";
import { FinancialDatas } from "./financial-datas";
import { FinancialData, FinancialDataType } from "./financial-data";

export enum ProjectStatus {
  ON_GOING = "ON_GOING",
  TERMINATED = "TERMINATED",
  CANCELLED = "CANCELLED",
  WAITING = "WAITING",
}

export enum ProjectStatusLabel {
  ON_GOING = "En Cours",
  WAITING = "En Attente",
  TERMINATED = "Terminé",
  CANCELLED = "Abandonné",
}

export class ProjectPhaseTask {
  task_id = "";
  name = "";
  desc = "";
  startDate: Date;
  endDate: Date;
  isCountWeekEnd: boolean;
  isPreciseTimes: boolean;
  employees_ids: string[] = [];
  free_employees: string[] = [];

  constructor() {
    this.task_id = generateObjectId();
    this.isCountWeekEnd = false;
    this.isPreciseTimes = false;
  }

  public static createInstance(jsonData: ProjectPhaseTask): ProjectPhaseTask {
    const t = Object.assign(new ProjectPhaseTask(), jsonData);

    t.startDate = jsonData.startDate ? new Date(jsonData.startDate) : null;
    t.endDate = jsonData.endDate ? new Date(jsonData.endDate) : null;

    return t;
  }

  public static createInstances(jsonData: ProjectPhaseTask[]): ProjectPhaseTask[] {
    if (!jsonData) return [];
    return jsonData.map(ProjectPhaseTask.createInstance);
  }
}

export class ProjectPhase {
  phase_id = "";
  name = "";
  desc = "";
  isOpen: boolean; //NOT saved in DB
  color = ""; //NOT saved in DB
  tasks: ProjectPhaseTask[] = [];

  constructor() {
    this.isOpen = false;
    this.phase_id = generateObjectId();
  }

  public static createInstance(jsonData: ProjectPhase): ProjectPhase {
    const p = Object.assign(new ProjectPhase(), jsonData);

    if (jsonData.tasks) {
      p.tasks = jsonData.tasks.map(ProjectPhaseTask.createInstance);
    }

    return p;
  }

  public static createInstances(jsonData: ProjectPhase[]): ProjectPhase[] {
    if (!jsonData) return [];
    return jsonData.map(ProjectPhase.createInstance);
  }
}
export class ProjectMilestone {
  milestone_id = "";
  name = "";
  desc = "";
  date: Date;

  constructor() {
    this.milestone_id = generateObjectId();
  }

  public static createInstance(jsonData: ProjectMilestone): ProjectMilestone {
    const m = Object.assign(new ProjectMilestone(), jsonData);

    m.date = jsonData.date ? new Date(jsonData.date) : null;

    return m;
  }

  public static createInstances(jsonData: ProjectMilestone[]): ProjectMilestone[] {
    if (!jsonData) return [];
    return jsonData.map(ProjectMilestone.createInstance);
  }
}

export class ProjectMaterial {
  material_id = "";
  employee_id = "";
  date: Date;
  quantity = 0;
  unit_price_ht = 0;
  name = "";
  characteristics = "";

  get total(): number {
    return (this.quantity || 0) * (this.unit_price_ht || 0);
  }

  constructor() {
    this.material_id = generateObjectId();
  }

  public static createInstance(jsonData: ProjectMaterial): ProjectMaterial {
    const m = new ProjectMaterial();
    m.material_id = jsonData.material_id || m.material_id;
    m.employee_id = jsonData.employee_id || "";
    m.date = jsonData.date ? new Date(jsonData.date) : null;
    m.quantity = jsonData.quantity || 0;
    m.unit_price_ht = jsonData.unit_price_ht || 0;
    m.name = jsonData.name || "";
    m.characteristics = jsonData.characteristics || "";

    return m;
  }

  public static createInstances(jsonData: ProjectMaterial[]): ProjectMaterial[] {
    if (!jsonData) return [];
    return jsonData.map(ProjectMaterial.createInstance);
  }
}

export class Project {
  project_id: string;
  project_corporate_id: string;

  eva_company_id: string;
  name: string = "";
  address_details: EvaAddress = new EvaAddress();
  address: string;
  address_lat: number;
  address_lng: number;
  desc: string = "";
  description: string = "";
  internal_contact: string = "";
  external_contact: string = "";
  allow_time_input: boolean = true;
  color: string;
  status: ProjectStatus;

  start_date: Date;
  end_date: Date;
  created_on?: Date;
  created_by?: string;
  updated_on?: Date;
  updated_by?: string;
  comment: string;

  allow_interventions: boolean = false;
  use_manager_badge_color: boolean = false;

  situations: ProjectSituation[];

  week_activities: EmployeeWeekActivity[];

  allowed_employees_id: string[] = [];

  project_managers_id: string[] = [];
  workforce_hourly_cost: number = 0.0;
  workforce_goal_margin: number = 0.0;
  workforce_budget_hours: number = null;
  workforce_days_worked: number = 0.0;
  workforce_total_days: number = 0.0;
  coverage_percent: number = 0.0;
  consumed_balance: number = 0.0;

  phases: ProjectPhase[] = [];
  milestones: ProjectMilestone[] = [];
  materials: ProjectMaterial[] = [];

  //semi-external data
  renew_project_name = "";
  renew_project_id = "";

  // external Data
  offers: Offer[];
  purchase_orders: PurchaseOrder[];
  employee_week_activities: EmployeeWeekActivity[];

  projected_cost = 0;
  projected_cx = 0;
  real_cost = 0;
  real_cx = 0;
  assets: Asset[];
  tags_ids: string[];
  tags: Tag[] = [];
  received_direct_invoices: Invoice[] = [];
  issued_free_invoices: Invoice[] = [];

  expenses: FinancialDatas;
  incomes: FinancialDatas;

  isOpened: boolean = true; // not saved in DB

  get excelExport() {
    return {
      id: this.project_corporate_id,
      name: this.name,
      address: this.address,
      desc: this.desc,
      internal_contact: this.internal_contact,
      external_contact: this.external_contact,
      status: this.status,
    };
  }

  // BONS DE COMMANDE EN COURS
  get pos_on_going(): PurchaseOrder[] {
    if (!this.purchase_orders) return [];
    return this.purchase_orders.filter((x) => x.status === PurchaseOrderStatus.ON_GOING);
  }
  get pos_on_going_count(): number {
    return this.pos_on_going.length;
  }
  get pos_on_going_value(): number {
    return this.pos_on_going.reduce((tot, item) => tot + +item.confirmation_net_price, 0);
  }

  // BONS DE COMMANDE TERMINES
  get pos_finished(): PurchaseOrder[] {
    if (!this.purchase_orders) return [];
    return this.purchase_orders.filter((x) => x.status === PurchaseOrderStatus.FINISHED);
  }
  get pos_finished_count(): number {
    return this.pos_finished.length;
  }
  get pos_finished_value(): number {
    return this.pos_finished.reduce((tot, item) => tot + +item.confirmation_net_price, 0);
  }

  // OFFRES EN COURS
  get offers_on_going(): Offer[] {
    if (!this.offers) return [];
    return this.offers.filter((x) => x.status === OfferStatus.ON_GOING);
  }
  get offers_on_going_count(): number {
    return this.offers_on_going.length;
  }
  get offers_on_going_value(): number {
    return this.offers_on_going.reduce((tot, item) => tot + +item.totalNet(), 0);
  }

  // OFFRES VALIDEES
  get offers_validated(): Offer[] {
    if (!this.offers) return [];
    return this.offers.filter((x) => x.status === OfferStatus.VALIDATED);
  }
  get offers_validated_count(): number {
    return this.offers_validated.length;
  }
  get offers_validated_value(): number {
    return this.offers_validated.reduce((tot, item) => tot + +item.totalNet(), 0);
  }

  /** Client à facturer : celui des offres acceptées, à défaut des offres non abandonnées, s'il est unique. */
  get billing_customer_id(): string | null {
    const offers = this.offers_validated.length
      ? this.offers_validated
      : (this.offers ?? []).filter((x) => x.status !== OfferStatus.CANCELLED);
    const customerIds = new Set(offers.map((x) => x.customer_id).filter(Boolean));
    return customerIds.size === 1 ? [...customerIds][0] : null;
  }

  // FACTURES EN COURS
  get invoices_on_going(): Invoice[] {
    let invoices: Invoice[] = [];
    if (!this.offers) return invoices;
    for (let o of this.offers) {
      if (o.invoices) {
        invoices = invoices.concat(
          o.invoices.filter((x) =>
            [InvoiceStatus.VALIDATED, InvoiceStatus.SENT, InvoiceStatus.PAID].includes(x.status),
          ),
        );
      }
    }
    return invoices;
  }
  get invoices_on_going_rate(): number {
    return (this.invoices_on_going_value / this.offers_validated_value) * 100 || 0;
  }
  get invoices_on_going_value(): number {
    return this.invoices_on_going.reduce((tot, item) => tot + +item.netTotalPrice, 0);
  }

  // FACTURES EN ATTENTE DE PAIEMENT
  get invoices_waiting_payment(): number {
    return this.invoices_on_going_value - this.invoices_paid_value;
  }

  // FACTURES PAYEES
  get invoices_paid(): Invoice[] {
    let invoices: Invoice[] = [];
    if (!this.offers) return invoices;
    for (let o of this.offers) {
      if (o.invoices) {
        invoices = invoices.concat(o.invoices.filter((x) => x.status === InvoiceStatus.PAID));
      }
    }
    return invoices;
  }
  get invoices_paid_rate(): number {
    return (this.invoices_paid_value / this.offers_validated_value) * 100 || 0;
  }
  get invoices_paid_value(): number {
    return this.invoices_paid.reduce((tot, item) => tot + +item.netTotalPrice, 0);
  }

  // COEF
  get margin_base_global(): number {
    let margin = this.margin_base_net();
    return margin ? margin : 0;
  }
  get margin_goal_global(): number {
    let margin = this.margin_goal_net();
    return margin ? margin : 0;
  }
  public get margin_global_projected(): number {
    let total = this.totalNet();

    let materialProjected = this.validPurchaseOrders.reduce((tot, item) => tot + item.confirmation_gross_price, 0);
    let workforceConsumed = this.getConsumedWorkforce();

    return total / (materialProjected + workforceConsumed);
  }
  get billing_coverage(): number {
    if (this.real_cost === 0) return 0;
    return (this.invoices_on_going_value / this.real_cost) * 100;
  }

  get billing_coverage_style(): string {
    if (this.billing_coverage >= 120) return "success";
    if (this.billing_coverage >= 100) return "warning";
    return "danger";
  }

  get validated_direct_invoices(): Invoice[] {
    return this.received_direct_invoices.filter((x) => x.status !== InvoiceStatus.CANCELLED);
  }

  constructor() {
    this.allowed_employees_id = [];
    this.situations = [];
    this.address_lat = 0.0;
    this.address_lng = 0.0;
    this.status = ProjectStatus.ON_GOING;
    this.isOpened = true;
  }

  /**
   * Chantier vierge portant les valeurs par défaut de la société.
   * Le back n'applique ces deux réglages ni à la création ni à l'édition : tout
   * écran web qui ouvre un formulaire de création doit passer par ici, sinon les
   * réglages société sont ignorés.
   */
  public static createWithCompanyDefaults(company: EvaCompany | null | undefined): Project {
    const p = new Project();
    p.use_manager_badge_color = company?.default_use_manager_badge_color ?? false;
    p.allow_interventions = company?.default_allow_interventions ?? false;
    return p;
  }

  public static createInstance(jsonData: Project): Project {
    const p = Object.assign(new Project(), jsonData);

    if (jsonData.offers) {
      p.offers = jsonData.offers.map(Offer.createInstance);
    }
    if (jsonData.tags) {
      p.tags = jsonData.tags.map(Tag.createInstance);
    }

    if (jsonData.assets) {
      p.assets = jsonData.assets.map(Asset.createInstance);
    }

    if (jsonData.address_details) {
      p.address_details = EvaAddress.createInstance(jsonData.address_details);
    }
    if (jsonData.expenses) {
      p.expenses = FinancialDatas.createInstance(jsonData.expenses);
    }
    if (jsonData.incomes) {
      p.incomes = FinancialDatas.createInstance(jsonData.incomes);
    }

    if (jsonData.employee_week_activities) {
      p.employee_week_activities = jsonData.employee_week_activities.map(EmployeeWeekActivity.createInstance);
    }

    if (jsonData.issued_free_invoices) {
      p.issued_free_invoices = jsonData.issued_free_invoices.map(Invoice.createInstance);
    }

    if (jsonData.purchase_orders) {
      p.purchase_orders = jsonData.purchase_orders.map(PurchaseOrder.createInstance);
    }
    if (jsonData.phases) {
      p.phases = jsonData.phases.map(ProjectPhase.createInstance);
    }
    if (jsonData.milestones) {
      p.milestones = jsonData.milestones.map(ProjectMilestone.createInstance);
    }
    if (jsonData.materials) {
      p.materials = ProjectMaterial.createInstances(jsonData.materials);
    }

    p.start_date = jsonData.start_date ? new Date(jsonData.start_date) : null;
    p.end_date = jsonData.end_date ? new Date(jsonData.end_date) : null;
    p.created_on = jsonData.created_on ? new Date(jsonData.created_on) : null;
    p.updated_on = jsonData.updated_on ? new Date(jsonData.updated_on) : null;

    return p;
  }

  public static createInstances(jsonData: any[]): Project[] {
    return jsonData.map(Project.createInstance);
  }

  get validWeekActivities(): EmployeeWeekActivity[] {
    if (!this.employee_week_activities) return [];
    return this.employee_week_activities.filter((x) => x.status === EmployeeWeekActivityStatus.ACCEPTED);
  }

  get validatedOffers(): Offer[] {
    if (!this.offers) return [];
    return this.offers.filter((x) => x.status === OfferStatus.VALIDATED);
  }

  get validPurchaseOrders(): PurchaseOrder[] {
    if (!this.purchase_orders) return [];
    return this.purchase_orders.filter((x) => x.status !== PurchaseOrderStatus.CANCELLED);
  }

  get validInvoices(): Invoice[] {
    let invoices: Invoice[] = [];
    for (let p of this.validPurchaseOrders) {
      if (p.invoices) {
        invoices = invoices.concat(p.invoices.filter((x) => x.status !== InvoiceStatus.CANCELLED));
      }
    }
    return invoices;
  }

  public totalCostPrice(filter = "*"): number {
    let total = 0.0;
    for (const o of this.validatedOffers) {
      total += o.totalCostPrice(filter);
    }
    return total;
  }

  totalSellingAuctionRate(filter = "*") {
    let base = 0;
    for (const o of this.validatedOffers) {
      base += o.totalSellingPriceWithoutDiscount(filter);
    }
    return Math.abs((this.totalSellingPriceWithAuction(filter) / base) * 100 - 100) || 0;
  }

  totalSellingPriceWithAuction(filter = "*") {
    let total = 0;
    for (const o of this.validatedOffers) {
      total += o.offer_item_groups.reduce((tot, item) => tot + +item.totalSellingPriceWithAuction(filter), 0);
    }
    return total;
  }

  /** Comme totalSellingPriceWithAuction(WORKFORCE), mais compte aussi la part MO des packs. */
  totalWorkforceSellingPriceWithAuction(): number {
    let total = 0;
    for (const o of this.validatedOffers) {
      total += o.totalWorkforceSellingPriceWithAuction();
    }
    return total;
  }

  public totalSellingPrice(filter = "*"): number {
    let total = 0.0;
    for (const o of this.validatedOffers) {
      total += o.totalSellingPrice(filter);
    }
    return total;
  }

  public totalNet(filter = "*"): number {
    let total = 0;
    for (const o of this.validatedOffers) {
      total += o.offer_item_groups.reduce((tot, item) => tot + +item.totalSellingPriceWithAuction(filter), 0);
    }
    return total;
  }

  public total_cost_price_with_discount_goal(filter = "*"): number {
    let total = 0;
    for (const o of this.validatedOffers) {
      total += o.total_cost_price_with_discount_goal(filter);
    }
    return total;
  }

  public margin_base_net(filter = "*"): number {
    return this.totalNet(filter) / this.totalCostPrice(filter);
  }

  public margin_goal_net(filter = "*"): number {
    return this.totalNet(filter) / this.total_cost_price_with_discount_goal(filter);
  }

  public getVatTotal(filter = "*"): number {
    return this.totalNet(filter) * 0.077;
  }

  public totalNetWithVat(filter = "*"): number {
    return this.totalNet(filter) + this.getVatTotal(filter);
  }

  public totalDiscounts(filter = "*"): number {
    let total = 0.0;
    for (const o of this.validatedOffers) {
      total += o.totalDiscounts(filter);
    }
    return total;
  }

  public totalBillingGross(filter = "*"): number {
    let total = 0.0;
    for (const o of this.validatedOffers) {
      total += o.total_billing_amount(filter);
    }
    return total;
  }

  public totalBillingPercent(filter = "*"): number {
    const billingNet = this.totalBillingNet(filter);
    const sellingNet = this.totalNet(filter);
    if (sellingNet === 0) return 0;
    return (billingNet / sellingNet) * 100 || 0;
  }

  public totalBillingNet(filter = "*"): number {
    let total = 0.0;
    for (const o of this.validatedOffers) {
      total += o.total_billing_amount_net(filter);
    }
    return total;
  }

  public totalBillingNetCurrentSituation(filter = "*"): number {
    let total = this.totalBillingNet(filter);

    for (const s of this.situations) {
      total -= s.totalBillingNet();
    }

    return total;
  }

  public getFormErrors(): string[] {
    const errors = [];
    if (this.isEmpty(this.name)) errors.push("Le nom ne peut être vide");
    if (this.isEmpty(this.address)) errors.push("L'adresse ne peut être vide");
    if (this.isEmpty(this.internal_contact)) errors.push("Le contact interne ne peut être vide");
    if (this.isEmpty(this.external_contact)) errors.push("Le contact externe (client) ne peut être vide");
    return errors;
  }

  public isEmpty(val: string): boolean {
    if (val == null) return true;
    if (val.trim() === "") return true;
    return false;
  }

  get displayName() {
    return this.project_corporate_id ? this.project_corporate_id + " - " + this.name : this.name;
  }

  public getConsumedMaterial() {
    let total = 0.0;

    if (this.validPurchaseOrders) {
      for (let p of this.validPurchaseOrders) {
        if (p.purchase_order_type === PurchaseOrderType.MATERIAL) {
          total += p.consumedAmount;
        }
      }
    }

    for (let i of this.validated_direct_invoices) {
      total += i.gross_total_price;
    }

    return total;
  }

  public getConsumedWorkforce() {
    let total = 0.0;

    if (this.employee_week_activities) {
      for (let wa of this.employee_week_activities.filter((x) =>
        [EmployeeWeekActivityStatus.ACCEPTED, EmployeeWeekActivityStatus.WAITING_VALIDATION].includes(x.status),
      )) {
        for (let a of wa.activities.filter((x) => x.project_id === this.project_id)) {
          total += a.activity_cost;
        }
      }
    }
    if (this.validPurchaseOrders) {
      for (let p of this.validPurchaseOrders) {
        if (
          p.purchase_order_type === PurchaseOrderType.WORKFORCE &&
          p.purchase_order_workforce_type === PurchaseOrderWorkForceType.SUBCONTRACTOR
        ) {
          total += p.consumedAmount;
        }
      }
    }

    return total;
  }

  get hoursWorked() {
    let total = 0.0;

    if (this.employee_week_activities) {
      for (let wa of this.employee_week_activities) {
        for (let a of wa.activities.filter(
          (x) => x.project_id === this.project_id && x.activity_type === EmployeeActivityType.WORKING,
        )) {
          total += a.hours_count;
        }
      }
    }

    return total;
  }

  public getConsumedRate(filter = "*") {
    let totalConsumed = this.getConsumed(filter);
    let total = this.totalNet(filter);
    return (totalConsumed / total) * 100 || 0;
  }

  public getConsumed(filter = "*") {
    let total = 0.0;

    if (filter === "*" || filter === OfferUnitType.MATERIAL) {
      total += this.getConsumedMaterial();
    }

    if (filter === "*" || filter === OfferUnitType.WORKFORCE) {
      total += this.getConsumedWorkforce();
    }

    return total;
  }

  public getConsumedProjectedMaterial() {
    let total = 0.0;

    if (this.validPurchaseOrders) {
      for (let p of this.validPurchaseOrders) {
        if (p.purchase_order_type === PurchaseOrderType.MATERIAL) {
          total += p.projectedAmount;
        }
      }
    }

    for (let i of this.validated_direct_invoices) {
      total += i.gross_total_price;
    }

    return total;
  }

  public getConsumedProjectedWorkforce() {
    let total = 0.0;

    if (this.employee_week_activities) {
      for (let wa of this.employee_week_activities.filter((x) => x.status !== EmployeeWeekActivityStatus.REJECTED)) {
        for (let a of wa.activities.filter((x) => x.project_id === this.project_id)) {
          total += a.activity_cost;
        }
      }
    }

    if (this.validPurchaseOrders) {
      for (let p of this.validPurchaseOrders) {
        if (
          p.purchase_order_type === PurchaseOrderType.WORKFORCE &&
          p.purchase_order_workforce_type === PurchaseOrderWorkForceType.SUBCONTRACTOR
        ) {
          total += p.projectedAmount;
        }
      }
    }

    return total;
  }

  public getConsumedProjectedRate(filter = "*") {
    let totalConsumed = this.getConsumedProjected(filter);
    let total = this.totalNet(filter);
    return (totalConsumed / total) * 100 || 0;
  }

  public getConsumedProjected(filter = "*") {
    let total = 0.0;

    if (filter === "*" || filter === OfferUnitType.MATERIAL) {
      total += this.getConsumedProjectedMaterial();
    }

    if (filter === "*" || filter === OfferUnitType.WORKFORCE) {
      total += this.getConsumedProjectedWorkforce();
    }

    return total;
  }

  public getInstantMargin(filter = "*") {
    const consumed = this.getConsumed(filter);
    return consumed !== 0 ? this.totalNet(filter) / consumed : 0.0;
  }
  public getProjectedMargin(filter = "*") {
    const projected = this.getConsumedProjected(filter);
    return projected !== 0 ? this.totalNet(filter) / projected : 0.0;
  }

  public getAvailableBase(filter = "*") {
    const diff = this.totalNet(filter) - this.getConsumed(filter);
    return diff >= 0 ? diff : 0.0;
  }

  public getAvailableGoal(filter = "*") {
    const diff = this.total_cost_price_with_discount_goal(filter) - this.getConsumed(filter);
    return diff >= 0 ? diff : 0.0;
  }

  public getShortTitle() {
    return this.project_corporate_id + " - " + this.name;
  }

  // FACTURE RECUES
  get posWithLateInvoices(): PurchaseOrder[] {
    return this.validPurchaseOrders.filter((x) => x.toPayLateInvoicesCount > 0);
  }
  get toPayLateInvoices(): Invoice[] {
    return this.validPurchaseOrders.flatMap((x) => x.toPayLateInvoices).filter((x) => x);
  }
  get toPayLateInvoicesCount(): number {
    return this.toPayLateInvoices.length;
  }
  get toPayLateInvoicesValue(): number {
    return this.toPayLateInvoices.reduce((tot, item) => (tot += item.netTotalPrice), 0);
  }
  get posWithNotLateInvoices(): PurchaseOrder[] {
    return this.validPurchaseOrders.filter((x) => x.toPayLateInvoicesCount === 0);
  }
  get toPayNotLateInvoices(): Invoice[] {
    return this.validPurchaseOrders.flatMap((x) => x.toPayNotLateInvoices).filter((x) => x);
  }
  get toPayNotLateInvoicesCount(): number {
    return this.toPayNotLateInvoices.length;
  }
  get toPayNotLateInvoicesValue(): number {
    return this.toPayNotLateInvoices.reduce((tot, item) => (tot += item.netTotalPrice), 0);
  }

  get workforce_total_hours() {
    let workforce_budget_goal = this.workforce_budget_goal;
    let workforce_hourly_cost = this.workforce_hourly_cost;
    return workforce_hourly_cost !== 0 ? workforce_budget_goal / workforce_hourly_cost : 0.0;
  }

  /* get workforce_total_days() {
    let workforce_total_hours = this.workforce_total_hours;
    return workforce_total_hours ? workforce_total_hours / 8.25 : 0.0;
  }

  get workforce_days_worked() {
    return this.hoursWorked / 8.25 || 0.0;
  }*/

  get workforce_days_available() {
    let base = this.workforce_total_days;
    let consumed = this.workforce_days_worked;
    return base > consumed ? base - consumed : 0;
  }

  get workforce_consumed_rate() {
    let workforce_total_days = this.workforce_total_days;
    return workforce_total_days !== 0 ? (this.workforce_days_worked / workforce_total_days) * 100 : 0.0;
  }

  get workforce_available_rate() {
    let workforce_total_days = this.workforce_total_days;
    return workforce_total_days !== 0 ? (this.workforce_days_available / workforce_total_days) * 100 : 0.0;
  }

  get workforce_budget_goal() {
    let sumWorkforce = this.totalWorkforceSellingPriceWithAuction();
    let goalWorkforce = sumWorkforce;
    if (this.workforce_goal_margin) {
      goalWorkforce -= (sumWorkforce * this.workforce_goal_margin) / 100;
    }
    return goalWorkforce;
  }

  get consumedProgressBarClass() {
    let workforce_consumed_rate = this.workforce_consumed_rate;
    if (workforce_consumed_rate > 100) {
      return "danger";
    } else if (workforce_consumed_rate >= 75) {
      return "warning";
    } else {
      return "success";
    }
  }

  clone(): Project {
    let p = Object.assign(new Project(), this);
    return p;
  }

  checkForm() {
    let formErrors = [];
    let formWarnings = [];

    if (!this.name || this.name.trim() === "") {
      formErrors.push("Le nom du chantier ne peut être vide");
    }

    if (this.status === ProjectStatus.TERMINATED) {
      for (let offer of this.offers_validated) {
        if (offer.percentBilled < 99.9) {
          formErrors.push(
            `L'offre ${offer.offer_corporate_id} (${offer.pdf_config.title}) doit être facturé à 100% et envoyé avant de pouvoir terminer le chantier`,
          );
        }
      }
    }

    // warning if trying to cancel offer with validated offer
    if (this.status === ProjectStatus.CANCELLED) {
      if (this.offers_validated.length > 0) {
        formWarnings.push(
          "Attention, vous êtes sur le point d'abandonner un chantier avec des offres acceptées. Toutes les offres suivantes seront abandonnées ainsi que les factures associées : ",
        );
      }
      for (let offer of this.offers_validated) {
        formWarnings.push(`- ${offer.offer_corporate_id} (${offer.pdf_config.title})`);
      }
    }

    return { formErrors: formErrors, formWarnings: formWarnings };
  }
}

function generateObjectId(): string {
  const timestamp = Math.floor(new Date().getTime() / 1000).toString(16);
  const randomHex = Array.from({ length: 16 }, () => Math.floor(Math.random() * 16).toString(16)).join("");

  return timestamp + randomHex;
}
